import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PublicNav from "../../components/layout/PublicNav";
import PublicFooter from "../../components/layout/PublicFooter";
import PageMeta from "../../components/seo/PageMeta";
import {
  ShieldCheck, Building2, Handshake, Scale, Globe2, Stethoscope,
  ArrowRight, ArrowUpRight, CheckCircle2, Sparkles, BadgeCheck,
  BarChart3, MapPin, Briefcase, Award,
} from "lucide-react";

/* ------------------------------------------------------------------ */
/*  Data                                                                */
/* ------------------------------------------------------------------ */

const CERTIFICATIONS = [
  {
    key: "nmsdc",
    name: "Minority Business Enterprise",
    short: "NMSDC · Renewal pending",
    status: "Renewal submitted",
    issuer: "NMSDC — National Minority Supplier Development Council",
    description: "NMSDC minority-business recertification submitted and awaiting issuer review. Procurement teams should validate current eligibility directly with the issuing council.",
    category: "National",
    image: "/certifications/nmsdc.png",
    testId: "cert-nmsdc",
  },
  {
    key: "hmsdc",
    name: "Houston Minority Supplier Development Council",
    short: "HMSDC · Status pending",
    status: "Verification pending",
    issuer: "HMSDC — NMSDC regional affiliate, Gulf Coast",
    description: "Prior HMSDC regional affiliation; current certification standing is under verification in conjunction with the NMSDC renewal.",
    category: "Regional",
    image: null,
    badgeText: "HMSDC",
    badgeSubtitle: "Gulf Coast Affiliate",
    testId: "cert-hmsdc",
  },
  {
    key: "mbe",
    name: "Minority Business Enterprise",
    short: "NCTRCA · MBE Certified",
    issuer: "NCTRCA — North Central Texas Regional Certification Agency",
    description: "NCTRCA MBE certification recorded through December 31, 2027, subject to current issuer verification and purchasing requirements.",
    category: "Texas",
    image: "/certifications/mbe.png",
    testId: "cert-mbe",
  },
  {
    key: "sbe",
    name: "Small Business Enterprise",
    short: "NCTRCA · SBE Certified",
    issuer: "NCTRCA — North Central Texas Regional Certification Agency",
    description: "NCTRCA SBE certification recorded through December 31, 2027, subject to current issuer verification and purchasing requirements.",
    category: "Size",
    image: "/certifications/sbe.jpeg",
    testId: "cert-sbe",
  },
  {
    key: "sam",
    name: "SAM.gov Registered",
    short: "Federal Contractor — Active",
    issuer: "System for Award Management (SAM.gov) · U.S. Federal Government",
    description: "SAM.gov registration recorded active through December 18, 2026 at the last internal review. Please verify current registration and contracting eligibility directly with SAM.gov.",
    category: "Federal",
    image: "/certifications/sam.png",
    testId: "cert-sam",
  },
];

const WHY_MATTERS = [
  {
    Icon: BarChart3,
    t: "Board-reported spend",
    d: "Supplier-diversity spend is a Joint Commission-aligned, board-visible metric. Verified diverse partners show up on the scorecard — unverified ones do not.",
  },
  {
    Icon: Scale,
    t: "Regulatory alignment",
    d: "Hospital-association, state Medicaid, and federal payer programs increasingly require diverse-supplier participation on contracted staffing spend.",
  },
  {
    Icon: Handshake,
    t: "Workforce representation",
    d: "Clinician workforces best serve communities that look like them. Sourcing through diverse partners widens candidate pools and closes representation gaps.",
  },
  {
    Icon: Globe2,
    t: "Community reinvestment",
    d: "Dollars flowing through minority- and small-business partners re-circulate in the communities your facilities serve — measurable, not aspirational.",
  },
];

const POSITIONING = [
  {
    Icon: BadgeCheck,
    t: "Documented supplier diversity",
    d: "NCTRCA MBE/SBE credentials, a recorded SAM.gov registration, and NMSDC/HMSDC renewal tracking, with verification evidence available for procurement review.",
  },
  {
    Icon: Stethoscope,
    t: "Built for healthcare",
    d: "We are not a general-staffing firm retrofitting a compliance deck. Healthcare credentialing, Joint Commission alignment, and clinical quality are our only business.",
  },
  {
    Icon: Briefcase,
    t: "Ready for Tier-1 reporting",
    d: "Standardized diverse-spend reporting package — monthly and quarterly — in formats aligned with leading hospital supplier-diversity programs.",
  },
  {
    Icon: ShieldCheck,
    t: "Accountable governance",
    d: "Named executive sponsor per account. Quarterly business reviews include diverse-spend tracking, clinical quality, and compliance exceptions on one page.",
  },
];

const PARTNERSHIP_VALUE = [
  { k: "Tier-1 recognition",  d: "Direct contract relationships that count against prime diverse-spend targets — not sub-tier pass-throughs." },
  { k: "Standardized reports", d: "Monthly spend reports in the format procurement teams already consume, with year-over-year trending." },
  { k: "Clinical parity",      d: "Same credentialing, same SLAs, same clinical outcomes as any prime vendor on your panel — without exception." },
  { k: "Audit-ready records",  d: "Certification documentation, W-9s, insurance certificates, and SOC-2 attestations stored for on-demand pull." },
  { k: "Community signal",     d: "Patient-facing proof points that procurement choices translate into the communities your facilities serve." },
  { k: "Flexible engagement",  d: "Prime, sub-tier, or tiered partnership models — structured to fit your supplier-diversity program architecture." },
];

const FACTS = [
  { k: "NMSDC · MBE",  d: "Renewal submitted · review pending" },
  { k: "HMSDC",        d: "Regional standing under verification" },
  { k: "NCTRCA",       d: "MBE + SBE · North Texas" },
  { k: "SAM.gov",      d: "Federal contractor · Active" },
];

/* ------------------------------------------------------------------ */
/*  Page                                                                */
/* ------------------------------------------------------------------ */

export default function Diversity() {
  return (
    <div className="min-h-screen bg-white" data-testid="diversity-page">
      <PageMeta
        title="Diversity & Supplier Diversity | TRITAL Care®"
        description="TRITAL Care® supports healthcare supplier diversity through NCTRCA MBE/SBE credentials, SAM.gov registration and ongoing NMSDC/HMSDC renewal verification."
        canonical="https://www.tritalcare.com/diversity-and-inclusion"
      />
      <PublicNav />

      {/* 1 · HERO --------------------------------------------------- */}
      <section className="relative overflow-hidden bg-trital-mesh" data-testid="diversity-hero">
        <div className="absolute inset-0 dot-grid opacity-60 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-20 lg:pt-20 lg:pb-24 grid lg:grid-cols-12 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="lg:col-span-7"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-card">
              <Sparkles className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8} />
              <span className="overline text-trital-navy">Supplier Diversity</span>
            </div>
            <h1 className="mt-6 font-display font-black text-5xl lg:text-[60px] leading-[1.02] tracking-tight text-slate-900 text-balance">
              Supplier diversity. <span className="block text-trital-navy">Built healthcare-only.</span>
              <span className="block text-trital-red">SAM.gov-registered.</span>
            </h1>
            <p className="mt-6 text-slate-600 max-w-xl leading-relaxed">
              TRITAL Care® (TOPTAL Care Inc.) holds <span className="font-semibold text-slate-900">NCTRCA MBE and SBE certifications</span> recorded through December 2027. Our <span className="font-semibold text-slate-900">NMSDC renewal has been submitted</span> and HMSDC standing is under verification. Our <span className="font-semibold text-slate-900">SAM.gov registration</span> was recorded active at last review. Current issuer records are available for procurement verification.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-trital-navy text-white font-semibold btn-lift hover:bg-trital-navy-deep"
                data-testid="diversity-hero-contact"
              >
                Engage procurement <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
              </Link>
              <a
                href="#certifications"
                className="inline-flex items-center px-6 py-3.5 rounded-full border border-slate-300 text-trital-navy font-semibold hover:bg-slate-50"
                data-testid="diversity-hero-certifications"
              >
                View certifications
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}
            className="lg:col-span-5"
          >
            <div className="rounded-3xl border border-slate-200 bg-white shadow-navy p-6 lg:p-7 relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-trital-medical-blue-soft blur-3xl" />
              <div className="relative">
                <div className="overline text-trital-red">At a glance</div>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  {FACTS.map(({ k, d }) => (
                    <div key={k} className="rounded-xl border border-slate-200 bg-white p-3">
                      <div className="font-display font-black text-[15px] text-trital-navy leading-tight">{k}</div>
                      <div className="mt-1 text-[11.5px] text-slate-600 leading-snug">{d}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-5 flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <div className="flex -space-x-1.5">
                    <img src="/certifications/nmsdc.png" alt="NMSDC" className="h-8 w-8 rounded-full bg-white border border-slate-200 object-contain p-0.5" />
                    <img src="/certifications/nctrca.png" alt="NCTRCA" className="h-8 w-8 rounded-full bg-white border border-slate-200 object-contain p-0.5" />
                    <img src="/certifications/sam.png" alt="SAM.gov" className="h-8 w-8 rounded-full bg-white border border-slate-200 object-contain p-0.5" />
                  </div>
                  <div className="text-[11.5px] text-slate-600 leading-snug">
                    NCTRCA <span className="font-semibold text-slate-900">MBE/SBE credentials</span> documented. NMSDC/HMSDC status pending verification; check current <span className="font-semibold text-slate-900">SAM.gov</span> standing.
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2 · WHY SUPPLIER DIVERSITY MATTERS ---------------------- */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24" data-testid="diversity-why">
        <div className="max-w-3xl">
          <div className="overline text-trital-red">Why it matters in healthcare</div>
          <h2 className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tight text-slate-900 text-balance">
            A procurement strategy. Not a campaign.
          </h2>
          <p className="mt-4 text-slate-600 max-w-2xl leading-relaxed">
            Healthcare supplier-diversity programs are measured on scorecards the board reviews quarterly. Verified, reportable, Tier-1 diverse spend is the only variety that moves those numbers — and patient-facing outcomes follow.
          </p>
        </div>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {WHY_MATTERS.map(({ Icon, t, d }, i) => (
            <motion.div
              key={t}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.35, delay: i * 0.05 }}
              className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card hover:-translate-y-1 hover:shadow-card-hover transition-all"
            >
              <div className="w-11 h-11 rounded-xl bg-trital-medical-blue-soft text-trital-navy flex items-center justify-center">
                <Icon className="w-5 h-5" strokeWidth={1.8} />
              </div>
              <div className="mt-5 font-display font-bold text-[16px] leading-snug text-slate-900">{t}</div>
              <p className="mt-2 text-[13px] text-slate-600 leading-relaxed">{d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 3 · POSITIONING ------------------------------------------ */}
      <section className="bg-slate-50 border-y border-slate-200" data-testid="diversity-positioning">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
            <div className="lg:col-span-7">
              <div className="overline text-trital-red">TRITAL Care positioning</div>
              <h2 className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tight text-slate-900 text-balance">
                A healthcare-only, dual-certified workforce partner.
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-slate-600 leading-relaxed">
                We compete on clinical credentialing and enterprise accountability. Our certifications are a procurement-program unlock — not a marketing veneer.
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {POSITIONING.map(({ Icon, t, d }, i) => (
              <motion.div
                key={t}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.35, delay: i * 0.04 }}
                className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card"
              >
                <div className="w-11 h-11 rounded-xl bg-trital-navy text-white flex items-center justify-center">
                  <Icon className="w-5 h-5" strokeWidth={1.8} />
                </div>
                <div className="mt-5 font-display font-bold text-[16px] text-slate-900">{t}</div>
                <p className="mt-2 text-[13px] text-slate-600 leading-relaxed">{d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 4 · CERTIFICATION CARDS --------------------------------- */}
      <section id="certifications" className="max-w-7xl mx-auto px-5 lg:px-8 py-24 scroll-mt-24" data-testid="diversity-certifications">
        <div className="max-w-3xl">
          <div className="overline text-trital-red flex items-center gap-1.5">
            <Award className="w-3 h-3" strokeWidth={1.8} /> Certifications
          </div>
          <h2 className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tight text-slate-900 text-balance">
            Verified through every authority you already reference.
          </h2>
          <p className="mt-4 text-slate-600 max-w-2xl leading-relaxed">
            Our <span className="font-semibold text-slate-900">NCTRCA MBE/SBE</span> certifications are recorded through December 2027. <span className="font-semibold text-slate-900">NMSDC/HMSDC</span> renewal verification remains pending. Verify current <span className="font-semibold text-slate-900">SAM.gov</span> standing before contracting.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {CERTIFICATIONS.map(({ key, name, short, issuer, description, category, status, image, badgeText, badgeSubtitle, testId }, i) => (
            <motion.article
              key={key}
              initial={{ opacity: 1, y: 0 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.35, delay: i * 0.05 }}
              className="group relative rounded-3xl bg-white border border-slate-200 shadow-card overflow-hidden flex flex-col"
              data-testid={testId}
            >
              <div className="absolute -top-16 -right-16 w-52 h-52 rounded-full bg-trital-medical-blue-soft blur-3xl opacity-60 group-hover:opacity-90 transition-opacity" />
              <div className="relative p-6 lg:p-7 flex flex-col gap-5 flex-1">
                <div className="flex items-start gap-4">
                  <div className="w-[88px] h-[88px] shrink-0 rounded-2xl bg-white border border-slate-200 p-2 flex items-center justify-center shadow-sm">
                    {image ? (
                      <img
                        src={image}
                        alt={`${name} — official certification badge`}
                        className="max-w-full max-h-full object-contain"
                        loading="lazy"
                        data-testid={`${testId}-badge`}
                      />
                    ) : (
                      <div
                        className="w-full h-full rounded-xl bg-gradient-to-br from-[#0A1F3F] via-[#0f2a55] to-[#1e4ba0] text-white flex flex-col items-center justify-center px-1"
                        data-testid={`${testId}-badge`}
                        aria-label={`${name} certification badge`}
                      >
                        <div className="font-display font-black text-[15px] leading-none tracking-tight">{badgeText}</div>
                        <div className="mt-1 text-[7.5px] uppercase tracking-[0.12em] text-white/80 font-bold text-center leading-tight">
                          {badgeSubtitle}
                        </div>
                        <div className="mt-1 h-[2px] w-6 bg-trital-red rounded-full" />
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-trital-medical-blue-soft text-trital-navy text-[10px] font-bold uppercase tracking-wider">
                        {category}
                      </span>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[10px] font-bold uppercase tracking-wider">
                        {status ? null : <CheckCircle2 className="w-3 h-3" strokeWidth={2} />} {status || "Recorded active"}
                      </span>
                    </div>
                    <h3 className="mt-2 font-display font-black text-[18px] leading-tight text-slate-900">
                      {name}
                    </h3>
                    <p className="mt-0.5 text-[12.5px] font-semibold text-trital-navy">{short}</p>
                  </div>
                </div>
                <p className="text-[13.5px] text-slate-600 leading-relaxed">{description}</p>
                <div className="mt-auto flex items-start gap-2 pt-4 border-t border-slate-100">
                  <Building2 className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" strokeWidth={1.8} />
                  <span className="text-[11.5px] text-slate-500 leading-snug">{issuer}</span>
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        <div className="mt-8 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-slate-50 border border-slate-200 text-[12.5px] text-slate-600">
          <Sparkles className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8} />
          Hospital supplier-diversity teams can request signed certification letters and W-9s on demand via
          <Link to="/contact" className="font-semibold text-trital-navy hover:underline ml-1">partners@tritalcare.com</Link>.
        </div>

        {/* Capability Statement Builder CTA */}
        <div className="mt-10 rounded-3xl overflow-hidden border border-slate-200 bg-white shadow-card relative" data-testid="diversity-capability-cta">
          <div className="absolute inset-0 bg-trital-navy-mesh opacity-[0.06] pointer-events-none" />
          <div className="relative grid lg:grid-cols-[1fr_auto] gap-6 items-center p-6 lg:p-8">
            <div>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-trital-red/10 text-trital-red text-[10.5px] font-bold uppercase tracking-[0.14em]">
                <Sparkles className="w-3 h-3" strokeWidth={2} /> New · procurement-ready
              </div>
              <h3 className="mt-3 font-display font-black text-2xl lg:text-[32px] tracking-tight text-slate-900 leading-tight text-balance">
                Build a <span className="text-trital-navy">personalized capability statement</span> — QR-verified, in one click.
              </h3>
              <p className="mt-3 text-slate-600 leading-relaxed max-w-2xl text-[14px]">
                Type your hospital name, pick the NAICS codes and contract vehicles that matter, then print to PDF or send the
                shareable link. Every document carries a unique QR code that confirms authenticity in real time — a first for
                healthcare-workforce supplier diversity.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link
                  to="/capability-statement"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-trital-navy text-white font-semibold btn-lift hover:bg-trital-navy-deep"
                  data-testid="diversity-capability-cta-build"
                >
                  <Award className="w-4 h-4" strokeWidth={1.8} /> Build capability statement
                </Link>
                <Link
                  to="/capability-statement"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-slate-300 text-trital-navy font-semibold hover:bg-slate-50"
                  data-testid="diversity-capability-cta-preview"
                >
                  See the live preview <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
                </Link>
              </div>
            </div>
            <div className="hidden lg:flex flex-col items-center gap-2 pr-4">
              <div className="w-36 h-44 rounded-xl border border-slate-300 bg-white shadow-navy overflow-hidden flex flex-col">
                <div className="h-1.5 bg-trital-navy" />
                <div className="flex-1 p-2 space-y-1">
                  <div className="h-1 w-16 bg-slate-900 rounded-full" />
                  <div className="h-1 w-10 bg-slate-400 rounded-full" />
                  <div className="mt-2 grid grid-cols-2 gap-1">
                    <div className="h-6 rounded bg-slate-100" />
                    <div className="h-6 rounded bg-trital-medical-blue-soft" />
                    <div className="h-6 rounded bg-slate-100" />
                    <div className="h-6 rounded bg-slate-100" />
                  </div>
                  <div className="mt-1 h-1 w-14 bg-slate-300 rounded-full" />
                  <div className="h-1 w-20 bg-slate-300 rounded-full" />
                  <div className="h-1 w-16 bg-slate-300 rounded-full" />
                </div>
                <div className="p-1.5 border-t border-slate-200 flex items-center justify-end">
                  <div className="w-6 h-6 rounded-sm bg-trital-navy" />
                </div>
              </div>
              <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">1-page · Letter · QR-verified</div>
            </div>
          </div>
        </div>
      </section>

      {/* 5 · PARTNERSHIP VALUE ----------------------------------- */}
      <section className="bg-trital-navy-ink text-white relative overflow-hidden" data-testid="diversity-partnership">
        <div className="absolute inset-0 bg-trital-navy-mesh opacity-85 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-24 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="overline text-white/60">Partnership value</div>
            <h2 className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tight text-white text-balance">
              What procurement teams get, on paper.
            </h2>
            <p className="mt-4 text-white/75 leading-relaxed max-w-xl">
              Supplier-diversity impact measured in scorecard units — not PR copy. We report spend in the format your program already consumes, and we deliver clinical quality at parity with any prime on your panel.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {["Tier-1 direct contract", "Monthly spend reports", "Named exec sponsor"].map((t) => (
                <span key={t} className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full bg-white/10 text-white font-bold uppercase tracking-wider">
                  <BadgeCheck className="w-3 h-3" strokeWidth={1.8} /> {t}
                </span>
              ))}
            </div>
          </div>
          <ul className="lg:col-span-7 grid md:grid-cols-2 gap-3" data-testid="diversity-partnership-list">
            {PARTNERSHIP_VALUE.map(({ k, d }) => (
              <li key={k} className="rounded-xl border border-white/10 bg-white/5 backdrop-blur p-4">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-trital-red shrink-0" strokeWidth={1.8} />
                  <span className="font-display font-bold text-[15px] text-white">{k}</span>
                </div>
                <p className="mt-2 text-[13px] text-white/80 leading-relaxed">{d}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* 6 · CTA BAND --------------------------------------------- */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24" data-testid="diversity-cta-band">
        <div className="rounded-3xl bg-white border border-slate-200 p-8 lg:p-12 relative overflow-hidden shadow-card">
          <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-trital-medical-blue-soft blur-3xl" />
          <div className="absolute -bottom-20 -left-16 w-72 h-72 rounded-full bg-red-100 blur-3xl" />
          <div className="relative grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <div className="overline text-trital-red">Next step for procurement</div>
              <h3 className="mt-3 font-display font-black text-3xl lg:text-5xl tracking-tight text-slate-900 text-balance">
                Bring a certified healthcare partner on panel.
              </h3>
              <p className="mt-4 text-slate-600 max-w-xl leading-relaxed">
                We'll share our capability statement, certification letters, insurance certificates, and a sample Tier-1 spend report — no NDA gymnastics, no sales pressure.
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-2 text-[12px] text-slate-500">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200">
                  <MapPin className="w-3.5 h-3.5 text-trital-navy" strokeWidth={1.8} /> USA HQ · Missouri City, TX
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200">
                  <MapPin className="w-3.5 h-3.5 text-trital-navy" strokeWidth={1.8} /> Corporate · Stafford, TX
                </span>
              </div>
            </div>
            <div className="lg:col-span-5 flex flex-wrap gap-3 lg:justify-end">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-trital-navy text-white font-semibold btn-lift hover:bg-trital-navy-deep"
                data-testid="diversity-cta-procurement"
              >
                Engage procurement <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
              </Link>
              <Link
                to="/employers#request"
                className="inline-flex items-center px-6 py-3.5 rounded-full border border-slate-300 text-trital-navy font-semibold hover:bg-slate-50"
                data-testid="diversity-cta-rfp"
              >
                Request capability pack <ArrowUpRight className="w-4 h-4" strokeWidth={1.8} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
